import type { GroupView, SchedTask } from '../../api';

/**
 * The outline: phases with their tasks nested underneath.
 *
 * One flat, ordered list rather than a tree of components. Both panes iterate
 * the same array, so a row in the task list and a bar in the timeline are
 * guaranteed to be at the same index - which is what keeps them aligned no
 * matter how deep the outline goes.
 */
export type OutlineRow =
  | { kind: 'group'; id: string; depth: number; group: GroupView; hasChildren: boolean }
  | { kind: 'task'; id: string; depth: number; task: SchedTask };

export function buildOutline(
  groups: readonly GroupView[],
  tasks: readonly SchedTask[],
  collapsed: ReadonlySet<string>,
): OutlineRow[] {
  const childGroups = new Map<string | null, GroupView[]>();
  for (const group of groups) {
    const key = group.parentId;
    const list = childGroups.get(key) ?? [];
    list.push(group);
    childGroups.set(key, list);
  }
  for (const list of childGroups.values()) {
    list.sort((a, b) => a.code.localeCompare(b.code, 'de', { numeric: true }));
  }

  const tasksByGroup = new Map<string | null, SchedTask[]>();
  for (const task of tasks) {
    const key = task.wbsNodeId;
    const list = tasksByGroup.get(key) ?? [];
    list.push(task);
    tasksByGroup.set(key, list);
  }
  for (const list of tasksByGroup.values()) {
    list.sort(
      (a, b) =>
        a.sortOrder - b.sortOrder ||
        a.code.localeCompare(b.code, 'de', { numeric: true }),
    );
  }

  const rows: OutlineRow[] = [];

  const walk = (parentId: string | null, depth: number): void => {
    for (const group of childGroups.get(parentId) ?? []) {
      const children = childGroups.get(group.id) ?? [];
      const own = tasksByGroup.get(group.id) ?? [];
      rows.push({
        kind: 'group',
        id: group.id,
        depth,
        group,
        hasChildren: children.length > 0 || own.length > 0,
      });
      if (collapsed.has(group.id)) continue;
      walk(group.id, depth + 1);
      for (const task of own) {
        rows.push({ kind: 'task', id: task.id, depth: depth + 1, task });
      }
    }
  };

  walk(null, 0);

  // Tasks that hang free come last, at the top level, so they are visible
  // rather than quietly missing from the plan.
  for (const task of tasksByGroup.get(null) ?? []) {
    rows.push({ kind: 'task', id: task.id, depth: 0, task });
  }

  return rows;
}

/** Default bar colour, inherited from the nearest phase that sets one. */
export function colorFor(
  row: OutlineRow,
  groups: readonly GroupView[],
): string | null {
  if (row.kind === 'group') return row.group.color;
  if (row.task.color) return row.task.color;

  const byId = new Map(groups.map((group) => [group.id, group]));
  let current = row.task.wbsNodeId ? byId.get(row.task.wbsNodeId) : undefined;
  let guard = 0;
  while (current && guard++ < 100) {
    if (current.color) return current.color;
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return null;
}
